import React, { useState, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom'; // Import useParams to get groupId from URL
import { useNavigate } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { getAuthCookie } from "../../utils/authCookies";

interface Agent {
  user_id: string;
  name: string;
}

type Group = {
  id: string;
  name: string;
  payment_after_days: number;
  allow_partial_pay: boolean;
  max_credit_limit: number;
  discount_percent: number;
  allowed_user_ids: Agent[];
  status: string;
  domain: string;
  purpose: string;
  audience: string;
};
interface SignageEditGroupProps {
  groupId: string;
  onClose: () => void;
}

const SignageEditGroup: React.FC<SignageEditGroupProps> = ({ groupId, onClose }) => {
  const [groupData, setGroupData] = useState<any>(null);// Get groupId from URL params
  const token = getAuthCookie("sessionToken");
  const [group, setGroup] = useState<Group | null>(null); // State to hold the group data
  const [formSubmitting, setFormSubmitting] = useState(false);
  const navigate = useNavigate();
  // Fetch group details when groupId changes
  useEffect(() => {
    const fetchGroupData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/customer-groups/${groupId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        setGroup(data);

      } catch (error) {
        console.error('Error fetching group data:', error);
      }
    };

    if (groupId) {
      fetchGroupData();
    }
  }, [groupId]);

  // Initialize formik
  const formik = useFormik({
    initialValues: {
      groupName: group?.name || '',
      statuss: (group?.status),
      department: (group?.domain) || '',
      description: (group?.purpose) || '',
      isInternal: (group?.audience === "internal"),
    },
    enableReinitialize: true, // Enable reinitialization when group data is fetched
    validationSchema: Yup.object({
      groupName: Yup.string()
        .min(2, 'Group name must be at least 2 characters long')
        .max(50, 'Group name cannot be longer than 50 characters')
        .required('Group Name is required'),
      // statuss: Yup.string().required('Status is required'),
    }),
    onSubmit: async (values) => {
      console.log("Submitting form...", values);
      setFormSubmitting(true);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/customer-groups/${groupId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              name: values.groupName || "",
              status: values.statuss,
              domain: values.department || "",
              purpose: values.description || "",
              audience: values.isInternal ? "external" : "external",
            }),
          }
        );

        const result = await response.json().catch(() => ({} as { error?: string; message?: string }));

        if (!response.ok) {
          const backendMessage =
            (typeof result?.error === "string" && result.error) ||
            (typeof result?.message === "string" && result.message) ||
            "Failed to update Group.";
          showError(backendMessage);
          return;
        }

        showSuccess("Group updated successfully!");

        onClose(); // ✅ close modal

        // navigate("/signage/groups", {
        //   state: { successMessage: "Group updated successfully!" },
        // });

      } catch (err) {
        console.error(err instanceof Error ? err.message : "Unknown error");
        showError(err instanceof Error ? err.message : "Failed to update Group.");
      } finally {
        setFormSubmitting(false);
      }
    }
  });


  return (
    <div className="content">
      <div className="d-flex justify-content-between align-items-center mb-3 px-3 pt-3">
        <h5 className="modal-title fw-bold">Edit Group</h5>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={onClose} // ✅ use the passed function
        ></button>
      </div>
      <div className="row justify-content-center">
        <div className="col-md-12 col-lg-12">
          <div className="card shadow rounded-4 border-0">
            <div className="card-body p-4">
              <form onSubmit={formik.handleSubmit}>
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <label htmlFor="groupName" className="form-label fw-semibold">
                      Group Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control rounded-3 br-8"
                      id="groupName"
                      name="groupName"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.groupName}
                    />
                    {formik.touched.groupName && formik.errors.groupName && (
                      <div className="text-danger mt-1">{formik.errors.groupName}</div>
                    )}
                  </div>
                  {/* <div className="col-md-9">
                    <label htmlFor="department" className="form-label fw-semibold">
                      Department
                    </label>
                    <select
                      className="form-select rounded-3 br-8"
                      id="department"
                      name="department"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.department}
                    >
                      <option value="">- Select -</option>
                      <option value="admin">Admin</option>
                      <option value="front_desk">Front Desk</option>
                      <option value="accounting">Accounting</option>
                      <option value="installer">Installer</option>
                      <option value="operations">Operations</option>
                      <option value="warehouse">Warehouse</option>
                      <option value="marketing">Marketing</option>
                      <option value="legal">Legal</option>
                      <option value="management">Management</option>
                      <option value="sales">Sales</option>
                      <option value="hr">HR</option>
                      <option value="it">IT</option>
                      <option value="support">Support</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold d-block">Within organization</label>
                    <div className="form-check mt-1">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="isInternal"
                        name="isInternal"
                        onChange={formik.handleChange}
                        checked={formik.values.isInternal}
                        disabled={group?.audience === "internal"}
                        style={{ width: 18, height: 18, borderRadius: 4, appearance: 'auto' }}
                      />
                    </div>
                  </div> */}

                  {/* Description below */}
                  <div className="col-md-12 mb-3">
                    <label htmlFor="description" className="form-label fw-semibold mt-2">
                      Description
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      className="form-control rounded-3 br-8"
                      rows={3}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.description}
                    />
                  </div>
                  <div className="col-md-12">
                    <label htmlFor="statuss" className="form-label fw-semibold">
                      {/* Status <span className="text-danger">*</span> */}
                      Status
                    </label>
                    <select
                      className="form-select rounded-3 br-8"
                      id="statuss"
                      name="statuss"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.statuss}
                    >
                      <option value="">- Select -</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                    {formik.touched.statuss && formik.errors.statuss && (
                      <div className="text-danger mt-1">{formik.errors.statuss}</div>
                    )}
                  </div>
                  {/* Footer buttons: Cancel left, Update right */}
                  <div className="col-md-12 d-flex justify-content-between align-items-center mt-3">
                    <button
                      type="button"
                      className="btn back-btn surface-btn fw-bold fs-16"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary table-addbutton"
                      type="submit"
                      disabled={formSubmitting}
                    >
                      Update
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignageEditGroup;
